import { HumanTaskPayloadSchema, HumanTaskState } from 'flow-schema';
import { z } from 'zod';

import {
  CustomSubtaskBlockConfigSchema,
  type HumanTaskBlockConfig,
  HumanTaskBlockConfigSchema,
  HumanTaskPresentationMode,
  HumanTaskType,
} from './config.js';
import { HumanTaskParsedToolsSchema } from './parsedTools.js';
import { SubmitPriorAuthorizationParsedConfigSchema } from './submitPriorAuthorization.js';
import { oneOrMany } from '../../../utils.js';
import { SortOrder } from '../../common.js';
import { TagNameSchema } from '../tag.js';

export { HumanTaskType, HumanTaskPresentationMode };

export {
  type BulkTaskResult,
  BulkTaskResultSchema,
  BulkTaskStatus,
  type HumanTaskAssetPayload,
  HumanTaskAssetPayloadSchema,
  type HumanTaskAssetResponse,
  HumanTaskAssetResponseSchema,
  type HumanTaskBulkActionBody,
  HumanTaskBulkActionBodySchema,
  type HumanTaskBulkActionResponse,
  HumanTaskBulkActionResponseSchema,
  type HumanTaskPayload,
  HumanTaskPayloadSchema,
  HumanTaskState,
  type HumanTaskSubtaskStepPayload,
  HumanTaskSubtaskStepPayloadSchema,
  MAX_BULK_TASK_COUNT,
} from 'flow-schema';

export * from './submitPriorAuthorization.js';

export const CustomSubtaskParsedConfigSchema = CustomSubtaskBlockConfigSchema.extend({
  tools: HumanTaskParsedToolsSchema,
});
export type CustomSubtaskParsedConfig = z.infer<typeof CustomSubtaskParsedConfigSchema>;

export const HumanTaskParsedConfigSchema = z.discriminatedUnion('taskType', [
  CustomSubtaskParsedConfigSchema,
  SubmitPriorAuthorizationParsedConfigSchema,
]);
export type HumanTaskParsedConfig = z.infer<typeof HumanTaskParsedConfigSchema>;

export const HumanTaskSchema = z.object({
  id: z.string().uuid(),
  created: z.string().datetime(),
  updated: z.string().datetime().optional(),
  flowRunId: z.string().uuid(),
  blockId: z.string().uuid(),
  name: z.string(),
  config: HumanTaskBlockConfigSchema as z.ZodType<HumanTaskBlockConfig>,
  organizationId: z.string().uuid(),
  state: z.enum(HumanTaskState),
  assignedToId: z.string().uuid().nullish(),
  expiresAt: z.string().datetime(),
  payload: HumanTaskPayloadSchema.nullish(),
  parsedConfig: HumanTaskParsedConfigSchema.nullish(),
  categoryId: z.string().uuid().nullable(),
  externalId: z.string().uuid().nullish(),
  entityEhrInformationId: z.string().uuid().nullable(),
  entityEncounterId: z.string().uuid().nullable(),
  entityExternalDocumentPacketId: z.string().uuid().nullable(),
  entityPatientOrderId: z.string().uuid().nullable(),
});
export type HumanTask = z.infer<typeof HumanTaskSchema>;

export const HumanTaskSearchResultSchema = HumanTaskSchema.pick({
  // Omit payload and parsedConfig to save bandwidth / memory. This
  // intentionally uses pick instead of omit to match the select query.
  id: true,
  created: true,
  updated: true,
  flowRunId: true,
  blockId: true,
  name: true,
  config: true,
  organizationId: true,
  state: true,
  assignedToId: true,
  expiresAt: true,
  categoryId: true,
  externalId: true,
  entityEhrInformationId: true,
  entityEncounterId: true,
  entityExternalDocumentPacketId: true,
  entityPatientOrderId: true,
}).extend({
  flowId: z.string().uuid(),
  tags: z.array(z.string()).default([]),
});
export type HumanTaskSearchResult = z.infer<typeof HumanTaskSearchResultSchema>;

export const HumanTaskFindAllQuerySchema = z
  .object({
    agentId: z.string().uuid(),
    flowRunId: z.string().uuid(),
    flowId: oneOrMany(z.string().uuid()),
    entityId: oneOrMany(z.string().uuid()),
    organizationId: oneOrMany(z.string().uuid()),
    states: z.array(z.enum(HumanTaskState)).default([]).optional(),
    categoryId: oneOrMany(z.string().uuid()),
    assignedToId: oneOrMany(z.string().uuid()),
    tags: oneOrMany(TagNameSchema).optional(),
    q: z.string(),
    createdAfter: z.iso.datetime(),
    createdBefore: z.iso.datetime(),
    expiresAfter: z.iso.datetime(),
    expiresBefore: z.iso.datetime(),
    limit: z.coerce.number().int().positive(),
    offset: z.coerce.number().int().nonnegative(),
    sortModel: z.array(
      z.object({
        field: z.string(),
        sort: z.enum(SortOrder),
      })
    ),
  })
  .partial();
export type HumanTaskFindAllQuery = z.infer<typeof HumanTaskFindAllQuerySchema>;

export const HumanTaskClaimNextQuerySchema = HumanTaskFindAllQuerySchema.omit({
  states: true,
  limit: true,
  offset: true,
});
export type HumanTaskClaimNextQuery = z.infer<typeof HumanTaskClaimNextQuerySchema>;
